import { useCallback } from 'react';

import type {
  ApprovalAction,
  ApprovalData,
  ApprovalType,
} from 'ui-product-approvals-common';

import type {
  AppHubUserRecord,
  AppTeamRecord,
  BreezeAgentRecord,
} from '../types/CrmSearchUser';
import { captureException } from '../utils/sentryUtils';
import { assertApprovalDataResolved } from '../utils/assertApprovalDataResolved';
import { toApprovalData } from '../utils/approvalResponseAdapters';
import { useRejectApprovalMutation } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.hooks';
import { GET_LATEST_APPROVAL_QUERY } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.dfc.new';
import { APPROVAL_TYPE_TO_OBJECT_TYPE_ID } from 'ui-product-approvals-common/constants/approvalTypeIds';

interface UseRejectApprovalArgs {
  approvalType: ApprovalType;
  approvalAction: ApprovalAction;
  approvableObjectId: number | undefined;
  usersRecord: AppHubUserRecord;
  agentsRecord: BreezeAgentRecord;
  teamsRecord: AppTeamRecord;
  resolvedData: ApprovalData;
}

export const useRejectApproval = ({
  approvalType,
  approvalAction,
  approvableObjectId,
  usersRecord,
  agentsRecord,
  teamsRecord,
  resolvedData,
}: UseRejectApprovalArgs) => {
  const [rejectApprovalMutation] = useRejectApprovalMutation();

  const reject = useCallback(
    (note: string): Promise<ApprovalData> => {
      const validated = assertApprovalDataResolved(
        'useRejectApproval',
        resolvedData,
        approvableObjectId
      );
      return rejectApprovalMutation({
        variables: {
          approvalId: validated.resolvedData.id,
          rejectionNote: note,
        },
        update: (cache, { data: mutationData }) => {
          const approvalData = mutationData?.result?.approval;
          if (approvalData) {
            cache.writeQuery({
              query: GET_LATEST_APPROVAL_QUERY,
              variables: {
                approvalTypeId: APPROVAL_TYPE_TO_OBJECT_TYPE_ID[approvalType],
                approvalAction,
                approvableObjectId: validated.approvableObjectId,
              },
              data: { result: { approval: approvalData } },
            });
          }
        },
      })
        .then((result) => {
          const approval = result?.data?.result?.approval;
          if (approval) {
            return toApprovalData(
              approval,
              usersRecord,
              agentsRecord,
              approvalType,
              approvalAction,
              teamsRecord
            );
          } else {
            throw new Error(
              'rejectApproval succeeded but returned no approval data'
            );
          }
        })
        .catch((error) => {
          captureException(error, {
            extra: {
              fingerprint: 'useApprovalData-reject',
              approvalType,
              approvalAction,
              approvalId: validated.resolvedData.id,
            },
          });
          throw error;
        });
    },
    [
      rejectApprovalMutation,
      resolvedData,
      usersRecord,
      agentsRecord,
      teamsRecord,
      approvalType,
      approvalAction,
      approvableObjectId,
    ]
  );

  return reject;
};
